import {
  Column,
  FlexBox,
  Grid,
  MetricItem,
  space,
  theme,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import { t } from "i18next";
import type { FC, SVGProps } from "react";
import styledComponents from "styled-components";

import { ReactComponent as Co2Icon } from "../../public/material-icons/co2.svg";
import { ReactComponent as GasIcon } from "../../public/material-icons/gas-meter.svg";
import { ReactComponent as HumidityIcon } from "../../public/material-icons/invert-colors.svg";
import { ReactComponent as WeightIcon } from "../../public/material-icons/monitor-weight.svg";
import { ReactComponent as PercentIcon } from "../../public/material-icons/percent.svg";
import { ReactComponent as DoorIcon } from "../../public/material-icons/sensor-door.svg";
import { ReactComponent as SensorIcon } from "../../public/material-icons/sensors.svg";
import { ReactComponent as SpeedIcon } from "../../public/material-icons/speed.svg";
import { ReactComponent as TemperatureIcon } from "../../public/material-icons/thermostat.svg";
import { ReactComponent as WarningIcon } from "../../public/material-icons/warning.svg";
import { ReactComponent as WaterIcon } from "../../public/material-icons/water-drop.svg";
import { isChannelOutdated } from "../util/channel-data.tsx";
import type { Channel, MonitorChannel } from "../view-model";

// Map engineering units to icons
const unitIconMap: Record<string, FC<SVGProps<SVGSVGElement>>> = {
  "°C": TemperatureIcon,
  "°F": TemperatureIcon,
  K: TemperatureIcon,
  "%": PercentIcon,
  "%RH": HumidityIcon,
  "%rH": HumidityIcon,
  ppm: GasIcon,
  CO2: Co2Icon,
  "m/s": SpeedIcon,
  "l/min": SpeedIcon,
  "m³/h": SpeedIcon,
  Pa: SpeedIcon,
  hPa: SpeedIcon,
  bar: SpeedIcon,
  mbar: SpeedIcon,
  kg: WeightIcon,
  g: WeightIcon,
  mg: WeightIcon,
  ml: WaterIcon,
  l: WaterIcon,
};

function getIconForChannel(channel: Channel): FC<SVGProps<SVGSVGElement>> {
  // Check unit
  if (channel.unit && unitIconMap[channel.unit]) {
    return unitIconMap[channel.unit];
  }
  // Check if value is a discrete state (string value like "Closed", "Open")
  if (typeof channel.current?.value === "string") {
    const lower = channel.current.value.toLowerCase();
    if (lower.includes("open") || lower.includes("closed")) {
      return DoorIcon;
    }
  }
  // Default
  return SensorIcon;
}

const StyledRoomMonitor = styledComponents.div`
  aspect-ratio: 2 / 1;
  display: flex;
  flex-direction: column;
  gap: ${space(1)};
  padding: ${space(5)};
  border: 1px solid ${theme.colorPalette.grey200};
  overflow: hidden;
`;

const StyledRoomMonitorMessage = styledComponents.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  justify-items: center;
  height: 100%;
`;

const StyledColumn = styledComponents(Column)`
  border-right: 1px solid ${theme.colorPalette.grey300};
  padding-right: ${space(5)};
`;

const formatValue = (
  value?: string | number | boolean,
  unit?: string,
  precision = 1
) => {
  if (value === undefined) return "-";
  let formattedValue = value;
  if (typeof value === "number") {
    formattedValue = value.toFixed(precision);
  }
  if (!unit) return `${formattedValue}`;
  return `${formattedValue} ${t(`unit.${unit}`, unit)}`;
};

const renderError = (message: string) => (
  <StyledRoomMonitor>
    <StyledRoomMonitorMessage>
      <StyledIconContainer>
        <WarningIcon color={theme.color.warning} />
      </StyledIconContainer>
      <Typography size="xs" whiteSpace="pre-wrap">
        {message}
      </Typography>
    </StyledRoomMonitorMessage>
  </StyledRoomMonitor>
);

const StyledIconContainer = styledComponents.div`
  flex-shrink: 0;
  font-size: 24px;
  margin-right: 8px;
`;

type RoomMonitorProps = {
  loading: boolean;
  error?: string;
  channels: Channel[];
};

const calcValueEmpty = (channel: Channel | undefined): boolean => {
  if (!channel) return true;
  return channel.status === "orphaned";
};

const formatDelta = (
  currentValue?: number,
  pastValue?: number,
  unit?: string,
  precision = 0
): string => {
  if (currentValue === undefined || pastValue === undefined) return "-";
  const delta = currentValue - pastValue;
  let result = delta >= 0 ? "+" : "";
  result += delta.toFixed(precision);
  if (unit) result += ` ${unit}`;
  return result;
};

// Zusatzinfo unter dem Wert: Veränderung ggü. der letzten Stunde
// ("+1.1 %rH innerhalb der letzten Stunde"). Offline-Channels zeigen "Offline".
// `past` liegt nur auf MonitorChannel — der RoomMonitor bekommt solche.
const calcAdditionalInfo = (channel: Channel): string => {
  if (channel.status !== "on") return t("roommonitor.offline");
  const current = channel.current?.value;
  const past = (channel as MonitorChannel).past?.value;
  if (typeof current === "number" && typeof past === "number") {
    return `${formatDelta(
      current,
      past,
      t(channel.unit ?? "") as string,
      1
    )} ${t("roommonitor.since")}`;
  }
  return "";
};

export const RoomMonitor = ({ channels, error, loading }: RoomMonitorProps) => {
  if (error) return renderError(error);

  if (channels.length === 0 && !loading) {
    return renderError(t("roommonitor.noDevice"));
  }

  return (
    <StyledRoomMonitor>
      <Grid>
        {channels.slice(0, 2).map((channel, index) => {
          const ColumnComponent = index === 0 ? StyledColumn : Grid.Column;
          const Icon = getIconForChannel(channel);
          return (
            <ColumnComponent key={channel.id} width={6}>
              <MetricItem
                empty={calcValueEmpty(channel)}
                expectedValue={calcAdditionalInfo(channel)}
                isLoading={loading}
                outdated={isChannelOutdated(channel)}
                title={
                  <FlexBox alignItems="center" gap={space(1)}>
                    <Icon color={theme.colorPalette.grey600} fontSize="24" />
                    <Typography color="grey600">{channel.name}</Typography>
                  </FlexBox>
                }
                value={formatValue(channel.current?.value, channel.unit)}
              />
            </ColumnComponent>
          );
        })}
      </Grid>
    </StyledRoomMonitor>
  );
};
